import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { webApi, type HomeApiData } from '@/services/webApi'
import { mockHomeData } from '@/data/mock/home'
import type { HomePageData } from '@/types/home'
import { mapApiCategory, mapApiCity, normalizeProperty } from '@/lib/apiNormalize'
import { normalizeMediaUrl } from '@/lib/normalizeMediaUrl'
import { cityFallbackImage, applyCityImageFallbacks } from '@/lib/cityFallbacks'
import { readHomeApiCache, writeHomeApiCache } from '@/lib/homeDataCache'
import { useCityStore } from '@/store/cityStore'

const HOME_STALE_MS = 60_000
const HOME_GC_MS = 10 * 60_000

/** Merge live API sections with curated mock fallbacks. */
export function buildHomePageData(api?: HomeApiData): HomePageData {
  return {
    ...mockHomeData,
    premiumProjects: api?.premiumProjects?.length
      ? api.premiumProjects.map(normalizeProperty)
      : [],
    recommendedForSale: api?.recommendedForSale?.length
      ? api.recommendedForSale.map(normalizeProperty)
      : [],
    recommendedForRent: api?.recommendedForRent?.length
      ? api.recommendedForRent.map(normalizeProperty)
      : [],
    freshProjectSlides: (api?.freshProjectSlides ?? []).map((slide) => ({
      ...slide,
      image: normalizeMediaUrl(slide.image),
    })),
    popularCities: applyCityImageFallbacks(
      api?.popularCities?.length
        ? api.popularCities.map(mapApiCity)
        : mockHomeData.popularCities.map((city, index) => ({
            ...city,
            image: city.image || cityFallbackImage(city.slug, index),
          })),
    ),
    categories: api?.categories?.length
      ? api.categories.map(mapApiCategory)
      : mockHomeData.categories,
    blogs: (api?.blogs ?? []).map((blog) => ({
      ...blog,
      image: blog.image ? normalizeMediaUrl(blog.image) : blog.image,
    })),
  }
}

/**
 * Homepage data with graceful degradation:
 * sections backed by the API use live data, the rest stay on curated mock
 * content. Session + TanStack caches make repeat visits feel instant.
 */
export function useHomeData() {
  const cityId = useCityStore((s) => s.cityId)
  const sessionCache = useMemo(() => readHomeApiCache(cityId), [cityId])

  const query = useQuery({
    queryKey: ['web', 'home', cityId],
    queryFn: async () => {
      const api = await webApi.home(cityId ? { cityId } : undefined)
      writeHomeApiCache(cityId, api)
      return api
    },
    staleTime: HOME_STALE_MS,
    gcTime: HOME_GC_MS,
    initialData: sessionCache?.data,
    initialDataUpdatedAt: sessionCache?.updatedAt,
    // Always recheck so new city photos (e.g. Rajkot) replace stale session fallbacks.
    refetchOnMount: 'always',
  })

  const data = useMemo(() => buildHomePageData(query.data), [query.data])

  return {
    data,
    isLoading: query.isLoading && query.data === undefined,
    isError: query.isError,
  }
}
